import { useState } from 'react';
import toast from 'react-hot-toast';

import Picker from '../../../common/components/picker/picker';
import { Notice } from '../../../common/components/detail/detail';
import { searchUsers } from '../../users/contexts/users';
import { useAssignTask, useChangeWeight, useUnassignTask } from '../contexts/tasks';

import '../styles/direct.css';

// Directing work: who it belongs to, and where it sits in the queue.
//
// THE SENTENCE THIS PANEL EXISTS TO SAY, out loud and next to the controls:
// changing the weight or the assignment does not interrupt anything. It does
// not release a live reservation, does not move the state, does not shorten the
// deadline. It affects the NEXT selections. Without that written down, the
// first person to reassign a task somebody is working on will assume they just
// took it away from them.
//
// And the number is called «Peso de prioridad», not «Prioridad», because what
// it does is not what "priority" promises: higher first WITHIN ITS GROUP.
// Raising the weight of an unassigned task does not move it ahead of anybody's
// assigned work — for that you assign it. Somebody who does not know that finds
// out with a task that never comes up.

export default function DirectWork({ task }) {
    const assign = useAssignTask(task.id);
    const unassign = useUnassignTask(task.id);
    const changeWeight = useChangeWeight(task.id);

    const [weight, setWeight] = useState(String(task.priority ?? 0));
    const [userId, setUserId] = useState(
        task.assigned_to != null ? String(task.assigned_to) : '',
    );

    const busy = assign.isPending || unassign.isPending || changeWeight.isPending;
    const weightChanged = Number(weight) !== Number(task.priority ?? 0);

    const handleAssign = async () => {
        if (!userId) return;
        try {
            const result = await assign.mutateAsync(userId);
            toast.success(
                result?.changed === false
                    ? 'Ya estaba asignada a esa persona'
                    : 'Responsable asignado',
            );
        } catch (err) {
            // A 422 on `user_id` is the interesting one: it means that person
            // cannot reach this task, so their agent could never take it. The
            // backend's sentence says which of the three reasons it was, and
            // that is what gets shown.
            toast.error(err.fields?.user_id || err.message || 'No se pudo asignar');
        }
    };

    const handleUnassign = async () => {
        try {
            const result = await unassign.mutateAsync();
            setUserId('');
            toast.success(
                result?.changed === false
                    ? 'Ya estaba sin asignar'
                    : 'La tarea vuelve a la cola general',
            );
        } catch (err) {
            toast.error(err.message || 'No se pudo quitar la asignación');
        }
    };

    const handleWeight = async () => {
        try {
            const result = await changeWeight.mutateAsync(weight);
            toast.success(
                result?.changed === false
                    ? 'El peso ya era ese'
                    : 'Peso actualizado',
            );
        } catch (err) {
            toast.error(err.fields?.priority || err.message || 'No se pudo cambiar el peso');
        }
    };

    return (
        <section className="direct">
            <h3 className="direct__title">Dirigir el trabajo</h3>

            <Notice>
                Cambiar el peso o el responsable <strong>no interrumpe nada</strong>: no libera
                una reserva viva, no mueve el estado y no acorta su vencimiento. Afecta a las
                selecciones siguientes.
            </Notice>

            <div className="direct__row">
                <label className="direct__field">
                    <span className="direct__label">Responsable asignado</span>
                    <Picker
                        value={userId}
                        onChange={setUserId}
                        fetchOptions={searchUsers}
                        cacheKey="users"
                        labelOf={user => user.name || user.email}
                        subtitleOf={user => user.email}
                        selectedLabel={task.assigned_user?.name}
                        placeholder="Elegir persona..."
                        disabled={busy}
                    />
                    <span className="direct__hint">
                        De quién es el trabajo. <strong>No</strong> quiere decir que lo esté
                        ejecutando: eso es la reserva, y es otra cosa.
                    </span>
                </label>

                <div className="direct__buttons">
                    <button
                        type="button"
                        className="direct__button direct__button--primary"
                        onClick={handleAssign}
                        disabled={busy || !userId || String(userId) === String(task.assigned_to ?? '')}
                    >
                        {assign.isPending ? 'Asignando...' : 'Asignar'}
                    </button>
                    <button
                        type="button"
                        className="direct__button"
                        onClick={handleUnassign}
                        disabled={busy || task.assigned_to == null}
                    >
                        Quitar asignación
                    </button>
                </div>
            </div>

            <div className="direct__row">
                <label className="direct__field">
                    <span className="direct__label">Peso de prioridad</span>
                    <input
                        className="direct__input"
                        type="number"
                        min={0}
                        value={weight}
                        onChange={e => setWeight(e.target.value)}
                        disabled={busy}
                    />
                    <span className="direct__hint">
                        Mayor peso primero <strong>dentro de su grupo</strong>. El trabajo
                        asignado tiene precedencia sobre la cola general: subirle el peso a una
                        tarea sin asignar no la adelanta al trabajo de nadie — para eso se asigna.
                    </span>
                </label>

                <div className="direct__buttons">
                    <button
                        type="button"
                        className="direct__button direct__button--primary"
                        onClick={handleWeight}
                        disabled={busy || !weightChanged || Number(weight) < 0}
                    >
                        {changeWeight.isPending ? 'Guardando...' : 'Cambiar el peso'}
                    </button>
                </div>
            </div>

            {/* Said here because this is where somebody might expect otherwise:
                the weight travels alone, through its own route, and never drags
                a frozen specification with it. */}
            <p className="direct__foot">
                El peso viaja por su propia operación, así que cambiarlo en una tarea autorizada
                no intenta tocar su especificación congelada.
            </p>
        </section>
    );
}
